class DatePicker {
  constructor (type, isDateAvailableFunc, highlightDateFunc, dateSelectedFunc, inModal) {
    this.dateAndTime = date // dateAndTime module

    this.isSubscription = type === 'subscription'
    this.isSelfServe = type === 'weddings' || type === 'events'
    this.isDateAvailableFunc = isDateAvailableFunc
    this.highlightDateFunc = highlightDateFunc
    this.dateSelectedFunc = dateSelectedFunc

    this.date_input = document.querySelector('#date-input')
    this.selected_date_element = document.querySelector('#selected-date')
    this.dates_element = document.querySelector('#dates')
    this.mth_element = document.querySelector('#mth')
    this.next_mth_element = document.querySelector('#next-mth')
    this.prev_mth_element = document.querySelector('#prev-mth')
    this.days_of_week_element = document.querySelector('#days-of-week')
    this.days_element = document.querySelector('#days')
    this.date_message_container = document.querySelector('#date-message-container')
    this.date_message = document.querySelector('#date-message')
    this.date_icon = document.querySelector('#date-icon')
    this.edit_date = document.querySelector('#edit-date-btn')

    this.timeZone = 'America/Chicago'

    this.selectedMonth = this.currentDate.getMonth()
    this.selectedYear = this.currentDate.getFullYear()

    /** @type {string} YYYY-MM-DD */
    this.selectedDate = null
    this.date_icon_clone = null

    this.inModal = inModal // Used to set the z-index on the datepicker dropdown
    this.isDetached = false
    this.isHidden = false

    this.init()
  }

  get monthString() {
    return (this.selectedMonth + 1).toString().padStart(2, '0')
  }

  get currentMonth() {
    return new Date(new Date().toLocaleString("en-US", {timeZone: this.timeZone})).getMonth()
  }

  get currentYear() {
    return new Date(new Date().toLocaleString("en-US", {timeZone: this.timeZone})).getFullYear()
  }

  get currentDate() {
    return new Date(new Date().toLocaleString("en-US", {timeZone: this.timeZone}))
  }

  get selectedMonthName() {
    return ['January','February','March','April','May','June','July','August','September','October','November','December'][this.selectedMonth]
  }

  get daysInSelectedMonth() {
    return new Date(this.selectedYear, this.selectedMonth + 1, 0).getDate()
  }

  get selectedMonthFirstDayOfWeek() {
    return new Date(this.selectedYear, this.selectedMonth, 1).getDay()
  }

  /**
   * Get day of week name
   * 
   * @param {number} dayOfWeek 
   * @returns {string}
   */
  getDayOfWeekName(dayOfWeek) {
    return ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'][dayOfWeek]
  }

  getIsCurrentMonthAvailable() {
    const currentDaysInMonth = new Date(this.currentYear, this.currentMonth + 1, 0).getDate()
    const currentMonthFirstDayOfWeek = new Date(this.currentYear, this.currentMonth, 1).getDay()
    let dayOfMonth = 1
    let unavailableDates = 1
    for (let i = 0; i < currentDaysInMonth + currentMonthFirstDayOfWeek; i++) {
      if (i >= currentMonthFirstDayOfWeek) {
        const dateString = `${this.currentYear}-${(this.currentMonth + 1).toString().padStart(2, '0')}-${dayOfMonth.toString().padStart(2, '0')}`
        if (!this.isDateAvailableFunc(dateString)) unavailableDates++
        dayOfMonth++
      } 
    }
    if (unavailableDates === dayOfMonth) {
      return false
    }
    return true
  }

  /**
   * Show calendar datepicker dropdown
   */
  showDropdown() {
    if (!this.inModal) return
    if (!this.isDetached) {
      this.dates_element.remove()
      document.body.appendChild(this.dates_element)
      this.isHidden = false
      this.isDetached = true
    } else if (this.isHidden) {
      this.isHidden = false
      document.body.appendChild(this.dates_element)
    }
    
    this.adjustDropdownPosition()
  }

  /**
   * Adjust position when inside modal
   */
  adjustDropdownPosition() {
    if (this.isHidden || !this.inModal) return;
    const boundingRect = this.date_input.getBoundingClientRect();
    // Estimate dropdown height if not rendered yet
    const dropdownHeight = this.dates_element.offsetHeight || 350; // fallback height
    const spaceBelow = window.innerHeight - (boundingRect.top + boundingRect.height);
    const spaceAbove = boundingRect.top;

    let top;
    if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
      // Not enough space below, but enough above: show above
      top = boundingRect.top - dropdownHeight;
    } else {
      // Default: show below
      top = boundingRect.top + boundingRect.height;
    }

    // Clamp so it never goes off the top or bottom of the viewport
    top = Math.max(0, Math.min(top, window.innerHeight - dropdownHeight));

    this.dates_element.style.top = `${top}px`;
    this.dates_element.style.left = `${boundingRect.left}px`;
    this.dates_element.style.width = `${boundingRect.width}px`;
    this.dates_element.style.zIndex = this.inModal ? 55 : 15;
  }

  hideDropdown() {
    if (!this.inModal) return
    this.isHidden = true
    this.dates_element.remove()
  }

  init() {
    this.addEventListeners()
    this.refreshCalendarDates()
  }

  refreshCalendarDates() {
    this.populateDates()
    this.mth_element.textContent = this.selectedMonthName + ' ' + this.selectedYear
  }

  /**
   * Date selected in calendar
   * 
   * @param {string} dateString 
   */
  setSelectedDate(dateString) {
    const dateInfo = dateString.split('-')
    this.selectedYear = Number(dateInfo[0])
    this.selectedMonth = Number(dateInfo[1]) - 1
    this.refreshCalendarDates()

    const selectedDay = Number(dateInfo[2])
    const selectedDate = this.dates_element?.querySelector(`#day-${selectedDay}`)
    if (selectedDate) {
      this.selectDate(selectedDate)
    }
  }

  addEventListeners() {
    this.date_message_container.addEventListener('click', () => {
      this.resetDeliveryDate()
    })
    this.next_mth_element.addEventListener('click', () => {
      this.goToNextMonth()
      this.refreshCalendarDates()
    }) 
    this.prev_mth_element.addEventListener('click', () => {
      this.goToPrevMonth()
      this.refreshCalendarDates()
    })
    window.addEventListener('click', (e) => {
      const datePickerClassList = ['border', 'border-copper', 'border-solid'] 
      const selfServeModal = document.getElementById('modal-self-serve-products')

      if (this.date_message_container.contains(e.target) || this.dates_element.contains(e.target) || this.date_input.contains(e.target)) {
        if (!this.inModal) {
          this.dates_element.classList.remove('fixed')
          this.dates_element.classList.add('relative')
        } else {
          this.dates_element.classList.remove('relative')
          this.dates_element.classList.add('fixed')
        }
        this.date_input.classList.add(...datePickerClassList)
        this.dates_element.classList.remove('hidden')
        
        if (selfServeModal) {
          selfServeModal.scrollTo(0, selfServeModal.scrollHeight + 100)
        } else {
          const rect = this.dates_element.getBoundingClientRect();
          const offset = rect.bottom + window.scrollY; // Get the absolute bottom position of the date picker
          const viewportHeight = window.innerHeight; // Get the height of the viewport
          
          // Scroll so that the bottom of the date picker is at the bottom of the viewport plus a little extra space
          window.scrollTo({
              top: offset - viewportHeight + 20,
              behavior: 'smooth' // for smooth scrolling
          });
          
        }
        this.showDropdown()
      } else {
        if (this.selectedDate && this.date_message_container.classList.contains('hidden')) {
          const isSubscription = this.date_message_container.classList.contains('grid') ? 'grid' : 'flex'
          this.date_message_container.classList.replace('hidden', isSubscription)
          this.date_input.classList.add('hidden')
        }
        this.dates_element.classList.add('hidden')
        this.date_input.classList.remove(...datePickerClassList)
        this.hideDropdown()
      }
    })
    
    document.addEventListener('scroll', () => {
      this.adjustDropdownPosition()
    })
  }

  checkNextMonthAvailability() {
    let month = this.selectedMonth + 1
    let year = this.selectedYear
    if (month > 11) {
      month = 0;
      year = year + 1
    }
    let dayOfMonth = 1
    let unavailableDates = 1
    for (let i = 0; i < this.daysInSelectedMonth + this.selectedMonthFirstDayOfWeek; i++) {
      if (i >= this.selectedMonthFirstDayOfWeek) {
        const dateString = `${year}-${(month + 1).toString().padStart(2, '0')}-${dayOfMonth.toString().padStart(2, '0')}`
        if (!this.isDateAvailableFunc(dateString)) unavailableDates++
        dayOfMonth++
      } 
    }
    if (unavailableDates === dayOfMonth) return false
    return true
  }

  populateDates() {
    this.setMinMaxMonths()
    this.days_element.innerHTML = ''

    let dayOfMonth = 1
    for (let i = 0; i < this.daysInSelectedMonth + this.selectedMonthFirstDayOfWeek; i++) {
      const day_element = document.createElement('div')
      const dayClassList = ['flex', 'justify-center', 'items-center', 'text-base']

      let dayStatusClass = []
      if (i >= this.selectedMonthFirstDayOfWeek) {
        const dateString = `${this.selectedYear}-${this.monthString}-${dayOfMonth.toString().padStart(2, '0')}`
        if (this.isDateAvailableFunc(dateString)) {
          dayStatusClass = ['text-[#313131]', 'hover:text-white', 'hover:bg-copper', 'cursor-pointer']
          day_element.setAttribute('id', `day-${dayOfMonth}`)
        } else {
          dayStatusClass = ['pointer-events-none', 'text-gray-200']
        }
        if (this.highlightDateFunc(dateString)) { // Red highlight for special mothersday weekend
          dayStatusClass.push('bg-purple-400/20')
        }
        day_element.textContent = dayOfMonth
        day_element.addEventListener('click', (e) => {
          e.stopPropagation()
          e.preventDefault()
          this.selectDate(e.target)
        })
        dayOfMonth++
      } else {
        // disable anything before and after days in month
        dayStatusClass = ['pointer-events-none']
      }
      
      day_element.classList.add(... dayClassList.concat(dayStatusClass))
      this.days_element.appendChild(day_element)
    }
  }

  setMinMaxMonths() {
    const enableMonth = ['text-[#313131]', 'hover:bg-[#F3F3F3]']
    const disableMonth = ['text-gray-200', 'pointer-events-none']

    let minDate = new Date(this.currentYear, this.currentMonth, 1)
    if (!this.getIsCurrentMonthAvailable()) {
      if (this.selectedMonth === this.currentMonth && this.selectedYear === this.currentYear) this.goToNextMonth()

      minDate = this.dateAndTime.addMonths(minDate, 1)
    }

    const nextMonthAvailable = this.checkNextMonthAvailability()

    const minMonth = minDate.getMonth()
    const minYear = minDate.getFullYear()
    
    let maxMonth = this.dateAndTime.addMonths(this.currentDate, 3).getMonth()
    let maxYear = this.dateAndTime.addMonths(this.currentDate, 3).getFullYear()
    
    if (this.isSelfServe) {
      maxMonth = this.dateAndTime.addMonths(this.currentDate, 10).getMonth()
      maxYear = this.dateAndTime.addMonths(this.currentDate, 10).getFullYear()
    }

    if (this.inModal) {
      maxMonth = this.dateAndTime.addMonths(this.currentDate, 12).getMonth()
      maxYear = this.dateAndTime.addMonths(this.currentDate, 12).getFullYear()
    }
    

    if (this.selectedMonth === minMonth && this.selectedYear === minYear) {
      this.prev_mth_element.classList.remove(...enableMonth)
      this.prev_mth_element.classList.add(...disableMonth)
    } else {
      this.prev_mth_element.classList.remove(...disableMonth)
      this.prev_mth_element.classList.add(...enableMonth)
    }

    //determine to disable next month
    if ((this.selectedMonth === maxMonth && this.selectedYear === maxYear) || !nextMonthAvailable) {
      this.next_mth_element.classList.remove(...enableMonth)
      this.next_mth_element.classList.add(...disableMonth)
    } else {
      this.next_mth_element.classList.remove(...disableMonth)
      this.next_mth_element.classList.add(...enableMonth)
    }
  }

  goToNextMonth() {
    this.selectedMonth++;
    if (this.selectedMonth > 11) {
      this.selectedMonth = 0;
      this.selectedYear++;
    }
  }

  goToPrevMonth() {
    this.selectedMonth--;
    if (this.selectedMonth < 0) {
      this.selectedMonth = 11;
      this.selectedYear--;
    }
  }

  /**
   * Timezone set from pickup select or zipcode check
   * 
   * @param {string} timeZone 
   */
  updateTimeZone(timeZone) {
    this.timeZone = timeZone
    this.selectedMonth = this.currentMonth
    this.selectedYear = this.currentYear
    this.refreshCalendarDates()
  }

  selectDate(dayElement) {
    const selectedDay = dayElement.innerText
    const selectedDate = `${this.selectedMonth + 1}/${selectedDay}/${this.selectedYear}`
    const date = new Date(selectedDate)

    const dayOfWeek = date.getDay()
    const messageContainer = this.date_message_container
    this.date_icon_clone = this.date_icon_clone || this.date_icon.cloneNode(true)

    this.selected_date_element.dataset.value = selectedDate
    this.selectedDate = `${this.selectedYear}-${(this.selectedMonth + 1).toString().padStart(2, '0')}-${selectedDay.toString().padStart(2, '0')}`

    this.date_input.classList.add('hidden')
    this.hideDropdown()

    messageContainer.classList.replace('hidden', 'flex')
    if (this.isSubscription) {
      messageContainer.parentElement.classList.add('mr-4')
      this.date_message.innerText = `First Delivery: ${selectedDate}\nAll future deliveries on ${this.getDayOfWeekName(dayOfWeek)}s`
    } else {
      messageContainer.classList.add('h-14', 'bg-white', 'shadow-[0_0_3px_0_rgba(0,0,0,0.2)]', 'items-center')
      this.edit_date.classList.add('justify-self-end', 'pr-2')
      messageContainer.insertBefore(this.date_icon_clone, messageContainer.firstChild)
      this.date_message.classList.add('text-lg', 'pl-4')
      this.date_message.innerText = selectedDate
    }

    this.edit_date.classList.remove('hidden')
    this.emitDatePickerEvent()
  }

  resetDeliveryDate(ineligibleDate) {
    if (ineligibleDate) {
      this.selectedDate = null
      this.selected_date_element.classList.add('text-gray-400')
      this.selected_date_element.innerText = 'Select'
      this.selected_date_element.dataset.value = ''
    }
    const isSubscription = this.date_message_container.classList.contains('grid') ? 'grid' : 'flex'
    this.date_message_container.classList.replace(isSubscription, 'hidden')
    this.date_input.classList.remove('hidden')
    this.date_message_container.parentElement.classList.remove('mr-4')
    if (this.selected_date_element.dataset.value) {
      this.date_message.innerText = this.isSubscription ? `First Delivery: ${this.selected_date_element.dataset.value}\nAll future deliveries on ${this.getDayOfWeekName(this.currentDate.getDay())}s` : this.selected_date_element.dataset.value
      this.selected_date_element.innerText = this.selected_date_element.dataset.value
      this.selected_date_element.classList.remove('text-gray-400')
    }
    this.emitDatePickerEvent()
  }

  emitDatePickerEvent() {
    this.dateSelectedFunc(this.selectedDate)
  }
}
// export default DatePicker